import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';

import Button from '../components/Button';
import Hero from '../components/Hero';
import SectionHero from '../components/SectionHero';
import IndustryList from '../components/IndustryList';
import ContentBlock from '../components/ContentBlock';
import TestimonialCard from '../components/TestimonialCard';
import USPGrid from '../components/USPGrid';
import CardGrid from '../components/CardGrid';
import { DefaultLogoReel } from '../components/LogoReel';
import Section from '../components/Section';

const ScalingBusinessBaseTemplate = ({ pageContext: { page } }) => {
  const { usps, contentBlocks } = page;

  const { caseStudies, resources } = useStaticQuery(graphql`
    fragment authorData on GraphCMS_Author {
      name
      title
      bio
      photo {
        ...assetData
      }
    }

    fragment assetData on GraphCMS_Asset {
      id
      url
      handle
      width
      height
    }

    fragment resourceData on GraphCMS_Resource {
      id
      slug
      friendlySlug
      friendlyStartDate
      title
      subTitle
      truncatedSubTitle
      coverImage {
        ...assetData
      }
      speakers {
        ...authorData
      }
      content
      externalUrl
      location
      starts
      type
      linkPath
    }

    fragment caseStudyData on GraphCMS_CaseStudy {
      id
      slug
      friendlySlug
      title
      subTitle
      coverImage {
        ...assetData
      }
      excerpt
      hasContent
      content
      externalUrl
      company {
        name
        url
        logo {
          ...assetData
        }
      }
      customer {
        ...authorData
      }
      linkPath
    }

    {
      resources: allGraphCmsResource(
        filter: {
          remoteId: {
            in: ["ckc98ju5c08cp01078vlj1x2n", "ckc7ygv5k0ihn0154ti5oj00e"]
          }
        }
      ) {
        nodes {
          ...resourceData
        }
      }

      caseStudies: allGraphCmsCaseStudy(
        filter: {
          remoteId: {
            in: ["ck5npvw00zwwo0b66gamobfxe", "ckff51nns0kuy01492vda0nen"]
          }
        }
      ) {
        nodes {
          ...caseStudyData
        }
      }
    }
  `);

  return (
    <React.Fragment>
      <Section>
        <Hero {...page} alignment="left">
          <Button
            href="/contact"
            appearance="outline"
            colour="pink"
            size="large"
          >
            Let our experts show you how &#8594;
          </Button>
        </Hero>
      </Section>
      <DefaultLogoReel />
      <Section>
        <SectionHero
          title="Take your tech stack to the next level"
          description="You’re wasting your time and resources on technologies that don’t communicate with each other, prevent you from scaling, and fragment your content into inflexible siloes"
        />

        <USPGrid title={false} items={usps} />
      </Section>
      <Section>
        <SectionHero
          title="Embrace programmatic headless content management"
          description="Unleash the power of structured content from a central hub and deliver high-performance experiences across all your applications, websites, and other digital frontends"
        />
        <IndustryList entries={contentBlocks} Component={ContentBlock} />
      </Section>
      <Section colour="light">
        <SectionHero
          title="See how companies like yours are redefining the way in which they manage and deliver their content"
          description="Digital-first companies scaling at breakneck speeds rely on GraphCMS to deliver their digital experiences in a programmatic and effective manner across borders"
          ctas={[
            { href: '/case-studies', label: 'View customer success stories' },
          ]}
        />
        <IndustryList entries={caseStudies.nodes} Component={TestimonialCard} />
      </Section>
      <Section>
        <SectionHero
          title="Explore our resources to learn how GraphCMS can accelerate your time to market"
          description="Get up to speed implementing a scalable content architecture with our carefully crafted studies and experiences"
          ctas={[{ href: '/resources', label: 'Download GraphCMS resources' }]}
        />
        <CardGrid entries={resources.nodes} />
      </Section>
    </React.Fragment>
  );
};

export default ScalingBusinessBaseTemplate;
